import { ReactNode, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { twMerge } from 'tailwind-merge'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/pro-light-svg-icons'

// ============================================================================
// Modal
// ============================================================================
interface ModalProps {
  children: ReactNode
  /** When omitted, the backdrop and Escape key won't close the modal; its content must provide a way out. */
  onClose?: () => void
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  className?: string
  zIndex?: number
}

const sizeMap = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[90vw]'
}

export const Modal = ({ children, onClose, size = 'md', className, zIndex }: ModalProps) => {
  // Close on escape key
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.()
      }
    },
    [onClose]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [handleEscape])

  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      className='fixed inset-0 flex items-center justify-center'
      style={{ zIndex: zIndex ?? 9999 }}
    >
      {/* Backdrop */}
      <div
        className='absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200'
        onClick={onClose}
      />

      {/* Modal Content */}
      <div
        className={twMerge(
          'relative w-full mx-4 bg-white rounded-2xl shadow-2xl',
          'animate-in fade-in-0 zoom-in-95 duration-200',
          sizeMap[size],
          className
        )}
      >
        {children}
      </div>
    </div>,
    document.body
  )
}

// ============================================================================
// ModalHeader
// ============================================================================
interface ModalHeaderProps {
  children: ReactNode
  onClose?: () => void
  className?: string
}

export const ModalHeader = ({ children, onClose, className }: ModalHeaderProps) => {
  return (
    <div className={twMerge('flex items-center justify-between px-6 py-4 border-b border-slate-200', className)}>
      <h2 className='text-xl font-semibold text-slate-900'>{children}</h2>
      {onClose && (
        <button
          onClick={onClose}
          className='flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors'
          title='Close modal'
          type='button'
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}
    </div>
  )
}

// ============================================================================
// ModalBody
// ============================================================================
interface ModalBodyProps {
  children: ReactNode
  className?: string
}

export const ModalBody = ({ children, className }: ModalBodyProps) => {
  return <div className={twMerge('px-6 py-5', className)}>{children}</div>
}

// ============================================================================
// ModalFooter
// ============================================================================
interface ModalFooterProps {
  children: ReactNode
  className?: string
}

export const ModalFooter = ({ children, className }: ModalFooterProps) => {
  return (
    <div
      className={twMerge(
        'flex items-center justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-200 rounded-b-2xl',
        className
      )}
    >
      {children}
    </div>
  )
}

export default Modal
